import React from "react";
import { FaCheckCircle, FaClock, FaTimesCircle } from "react-icons/fa";

export const getStatusBadge = (status) => {
  const s = String(status || "").toLowerCase().trim();

  // 1. Completed
  if (s === "completed" || s === "complete") {
    return {
      bg: "bg-emerald-700 text-white border-emerald-800 font-semibold shadow-2xs",
      stripBg: "bg-emerald-500",
      icon: <FaCheckCircle className="text-white text-[8px] sm:text-[10px]" />,
      text: "Completed",
      isWorking: false,
    };
  }

  // 2. Pending (default in MongoDB if empty or pending)
  if (s === "pending" || !s) {
    return {
      bg: "bg-rose-700 text-white border-rose-800 font-semibold shadow-2xs",
      stripBg: "bg-rose-500",
      icon: <FaTimesCircle className="text-white text-[8px] sm:text-[10px]" />,
      text: "Pending",
      isWorking: false,
    };
  }

  // 3. Working / In Progress / Running / Accepted
  if (
    s === "accept" ||
    s === "accepted" ||
    s.includes("accept") ||
    s.includes("running") ||
    s.includes("working") ||
    s.includes("progress")
  ) {
    return {
      bg: "bg-blue-600 text-white border-blue-700 font-semibold shadow-2xs",
      stripBg: "bg-blue-500",
      icon: <FaClock className="text-white text-[8px] sm:text-[10px] animate-pulse" />,
      text: "Working",
      isWorking: true,
    };
  }

  // 4. On Hold
  if (s.includes("hold")) {
    return {
      bg: "bg-slate-600 text-white border-slate-700 font-semibold shadow-2xs",
      stripBg: "bg-slate-500",
      icon: <FaClock className="text-white text-[8px] sm:text-[10px]" />,
      text: "On Hold",
      isWorking: false,
    };
  }

  // 5. Checking to Senior
  if (s.includes("senior")) {
    return {
      bg: "bg-indigo-600 text-white border-indigo-700 font-semibold shadow-2xs",
      stripBg: "bg-indigo-500",
      icon: <FaClock className="text-white text-[8px] sm:text-[10px]" />,
      text: "Checking to Senior",
      isWorking: false,
    };
  }

  // 6. Checking to Support / Checking / Chaking
  if (s.includes("support") || s.includes("check") || s.includes("chak")) {
    return {
      bg: "bg-violet-600 text-white border-violet-700 font-semibold shadow-2xs",
      stripBg: "bg-violet-500",
      icon: <FaClock className="text-white text-[8px] sm:text-[10px]" />,
      text: "Checking to Support",
      isWorking: false,
    };
  }

  // 7. Solved By Dynamic / Under Review
  if (s.includes("solve") || s.includes("dynamic") || s.includes("review")) {
    return {
      bg: "bg-cyan-700 text-white border-cyan-800 font-semibold shadow-2xs",
      stripBg: "bg-cyan-500",
      icon: <FaClock className="text-white text-[8px] sm:text-[10px]" />,
      text:
        s.includes("solve") || s.includes("dynamic")
          ? "Solved By Dynamic"
          : status || "Under Review",
      isWorking: false,
    };
  }

  // 8. Rejected
  if (s.includes("reject")) {
    return {
      bg: "bg-rose-800 text-white border-rose-900 font-semibold shadow-2xs",
      stripBg: "bg-rose-700",
      icon: <FaTimesCircle className="text-white text-[8px] sm:text-[10px]" />,
      text: "Rejected",
      isWorking: false,
    };
  }

  // Fallback for any other custom status
  return {
    bg: "bg-[#154D75] text-white border-[#0f3856] font-semibold shadow-2xs",
    stripBg: "bg-[#154D75]",
    icon: <FaClock className="text-white text-[8px] sm:text-[10px]" />,
    text: status || "Pending",
    isWorking: false,
  };
};

export const getAssigneeInfo = (call) => {
  if (!call) return { name: "", label: "Employee", role: "" };

  const isCompleted = (call.Status || "").toLowerCase() === "completed";

  // Check SuportTeamId
  if (call.SuportTeamId) {
    const name =
      call.SuportTeamId.Name ||
      call.SuportTeamId.OwnerName ||
      (typeof call.SuportTeamId === "string" ? call.SuportTeamId.trim() : "");
    const role = (call.SuportTeamId.Role || "").toLowerCase();
    const isSupport = role.includes("support");
    const label = isCompleted
      ? "Completed By"
      : isSupport
        ? "Support Team"
        : "Employee";
    if (name) return { name, label, role: call.SuportTeamId.Role || "" };
  }

  // Check SeniorDevloperID
  if (call.SeniorDevloperID) {
    const name =
      call.SeniorDevloperID.Name ||
      call.SeniorDevloperID.OwnerName ||
      (typeof call.SeniorDevloperID === "string" ? call.SeniorDevloperID.trim() : "");
    const role = (call.SeniorDevloperID.Role || "").toLowerCase();
    const isSupport = role.includes("support");
    const label = isCompleted
      ? "Completed By"
      : isSupport
        ? "Support Team"
        : "Employee";
    if (name) return { name, label, role: call.SeniorDevloperID.Role || "" };
  }

  // Check Solve_By / SolvedBy / Complete_By / CompletedBy
  const solver =
    call.Solve_By || call.SolvedBy || call.Complete_By || call.CompletedBy;
  if (solver) {
    const name =
      solver.Name ||
      solver.OwnerName ||
      (typeof solver === "string" ? solver : "");
    const role = (solver.Role || "").toLowerCase();
    const isSupport = role.includes("support");
    const label = isCompleted
      ? "Completed By"
      : isSupport
        ? "Support Team"
        : "Employee";
    if (name) return { name, label, role: solver.Role || "" };
  }

  if (Array.isArray(call.Record) && call.Record.length > 0) {
    const lastRec = call.Record[call.Record.length - 1];
    const name =
      lastRec?.Name ||
      lastRec?.EmpName ||
      lastRec?.User ||
      lastRec?.InHouseId?.Name;
    const role = (lastRec?.InHouseId?.Role || "").toLowerCase();
    const isSupport = role.includes("support");
    const label = isCompleted
      ? "Completed By"
      : isSupport
        ? "Support Team"
        : "Employee";
    if (name) return { name, label, role: lastRec?.InHouseId?.Role || "" };
  }

  if (typeof call.Employee === "string" && call.Employee.trim()) {
    return {
      name: call.Employee.trim(),
      label: isCompleted ? "Completed By" : "Employee",
      role: "",
    };
  }

  return { name: "", label: isCompleted ? "Completed By" : "Employee", role: "" };
};

const CalendarCallCard = ({ call, isExpanded, onClick }) => {
  const statusBadge = getStatusBadge(call.Status);
  const assigneeInfo = getAssigneeInfo(call);

  const projectName =
    call.Project?.project_name ||
    call.Project?.ProjectName ||
    (typeof call.Project === "string" ? call.Project : "");

  return (
    <div
      onClick={() => onClick && onClick(call)}
      className="group text-left p-2 rounded-lg transition-all duration-150 cursor-pointer overflow-hidden bg-white hover:bg-blue-50/40 border border-slate-300 hover:border-[#154D75] shadow-2xs text-slate-800"
    >
      {/* Top Row: Project Badge (if any) + Call Number */}
      <div className="flex items-center justify-between gap-1.5 mb-1.5">
        {projectName ? (
          <>
            <span
              className="text-[10px] sm:text-[11px] font-semibold tracking-tight truncate uppercase text-[#154D75] bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200 shrink min-w-0"
              title={projectName}
            >
              {projectName}
            </span>
            <div className="text-[11px] sm:text-xs shrink-0 whitespace-nowrap">
              <span className="font-medium text-slate-600">Call No</span>{" "}
              <span className="font-bold text-slate-900">: {call.CallNo || "N/A"}</span>
            </div>
          </>
        ) : (
          <div className="text-[11px] sm:text-xs">
            <span className="font-medium text-slate-600">Call No</span>{" "}
            <span className="font-bold text-slate-900">: {call.CallNo || "N/A"}</span>
          </div>
        )}
      </div>

      {/* Requirement / Issue */}
      {(() => {
        const displayReq =
          (call.Requirement || "").trim() ||
          (call.PresentStatus || "").trim() ||
          "-";
        return isExpanded ? (
          // EXPANDED VIEW
          <div className="space-y-1.5 my-1 text-[11px] sm:text-xs leading-snug">
            <div className="break-words">
              <span className="font-semibold text-slate-900">Requirement:</span>{" "}
              <span className="font-normal text-slate-700">{displayReq}</span>
            </div>

            {call.Solution && (
              <div className="break-words p-1.5 rounded-md border border-emerald-400 bg-emerald-50 text-emerald-900 font-normal text-[10px] sm:text-[11px]">
                <span className="font-semibold text-emerald-950">Solution:</span>{" "}
                <span>{call.Solution}</span>
              </div>
            )}
          </div>
        ) : (
          // COLLAPSED VIEW: Short preview
          <div
            className="text-[11px] sm:text-xs line-clamp-3 break-words leading-snug mb-1.5 font-normal text-slate-700 group-hover:text-slate-900"
            title={displayReq}
          >
            <span className="font-semibold text-slate-900">Req:</span> {displayReq}
          </div>
        );
      })()}

      {/* Status & Completed By / Employee / Support Team Footer */}
      <div className="pt-1.5 border-t border-slate-200 text-[10px] sm:text-[11px] space-y-1">
        <div className="flex items-center justify-between gap-1">
          <span className="font-medium text-slate-600">Status</span>
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded font-semibold border text-[10px] sm:text-[11px] ${statusBadge.bg}`}
          >
            {statusBadge.icon}
            <span className="whitespace-nowrap">{statusBadge.text}</span>
          </span>
        </div>

        {assigneeInfo.name && (
          <div className="flex items-center justify-between gap-1 pt-0.5 border-t border-dashed border-slate-200 text-slate-600">
            <span className="font-medium text-slate-600 shrink-0">
              {assigneeInfo.label}
            </span>
            <span
              className="font-bold text-[#154D75] truncate text-right max-w-[65%]"
              title={`${assigneeInfo.label}: ${assigneeInfo.name}${assigneeInfo.role ? ` (${assigneeInfo.role})` : ""}`}
            >
              : {assigneeInfo.name}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarCallCard;
